/**
 * Stock count — design/PROTOTYPE-SPEC.md §7.
 *
 * Walk the shelf, type what you can physically see, pick a reason for anything
 * that disagrees, apply. Blank means "not counted" and is skipped entirely, so a
 * partial count is a normal thing to do rather than an error.
 *
 * The system quantity each row was drawn with is sent back as `expectedQty`. If
 * someone logs a treatment while the shelf is being walked, the server refuses
 * that line instead of quietly erasing their work, and this page shows exactly
 * which rows to recheck.
 */
import { useEffect, useMemo, useState } from 'react';
import { VARIANCE_REASONS, VARIANCE_REASON_LABEL } from '@inventory-tracker/core';
import {
  applyStockCount,
  listProducts,
  StaleCountError,
  type ProductRow,
  type StockCountLine,
  type VarianceReason,
} from '../lib/api.js';
import { c, field, primaryButton, sans } from '../theme.js';
import { cardStyle } from './format.js';

interface Entry {
  counted: string;
  reason: VarianceReason | '';
  reasonNote: string;
}

const blank = (): Entry => ({ counted: '', reason: '', reasonNote: '' });

export function StockCountPage({
  onApplied,
  refreshToken,
}: {
  onApplied: () => void;
  refreshToken: number;
}) {
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [entries, setEntries] = useState<Record<string, Entry>>({});
  const [note, setNote] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** What is still needed before the count can be applied; shown by the button. */
  const [nudge, setNudge] = useState<string | null>(null);
  const [staleIds, setStaleIds] = useState<Set<string>>(new Set());
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  async function reload() {
    const list = await listProducts();
    setRows(list.products);
    setLoading(false);
  }

  useEffect(() => {
    void reload().catch((e: unknown) =>
      setError(e instanceof Error ? e.message : 'Could not load your products'),
    );
    // Booking stock in from the header changes the system quantities this sheet
    // was drawn against, and those are what `expectedQty` is checked on. Without
    // this the count is submitted against numbers the shell already knows are
    // stale and comes back 409.
  }, [refreshToken]);

  const entryFor = (id: string) => entries[id] ?? blank();

  function update(id: string, patch: Partial<Entry>) {
    setEntries((prev) => ({ ...prev, [id]: { ...(prev[id] ?? blank()), ...patch } }));
    // Touching a flagged row clears its warning — the counter is dealing with it.
    if (staleIds.has(id)) {
      setStaleIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }

  /** A row is "counted" only when something was actually typed. */
  const counted = useMemo(
    () => rows.filter((r) => entryFor(r.id).counted.trim() !== ''),
    [rows, entries],
  );

  const varianceOf = (row: ProductRow): number | null => {
    const raw = entryFor(row.id).counted.trim();
    if (raw === '') return null;
    const n = Math.max(0, Math.round(Number(raw)));
    if (!Number.isFinite(n)) return null;
    return n - row.qtyOnHand;
  };

  const differences = counted.filter((r) => (varianceOf(r) ?? 0) !== 0);
  const missingReason = differences.filter((r) => entryFor(r.id).reason === '');

  /**
   * The button is never dead. A greyed-out button that ignores clicks reads as
   * a frozen page (Emmanuel's review, 13 October 2026); pressing it with
   * something missing now says what is missing.
   */
  async function submit() {
    setDone(null);
    if (counted.length === 0) {
      setNudge('Type what you counted for at least one product first.');
      return;
    }
    if (missingReason.length > 0) {
      setNudge(
        `Pick a reason for each difference. ${missingReason.length} still ${
          missingReason.length === 1 ? 'needs' : 'need'
        } one: ${missingReason
          .slice(0, 3)
          .map((r) => r.name)
          .join(', ')}${missingReason.length > 3 ? ' and more' : ''}. They are outlined in the table.`,
      );
      return;
    }
    setNudge(null);
    setBusy(true);
    setError(null);
    try {
      const lines: StockCountLine[] = counted.map((r) => {
        const e = entryFor(r.id);
        const qty = Math.max(0, Math.round(Number(e.counted)));
        const line: StockCountLine = {
          productId: r.id,
          countedQty: qty,
          // What this row was drawn with, so the server can spot a stale sheet.
          expectedQty: r.qtyOnHand,
        };
        if (qty !== r.qtyOnHand && e.reason) line.reason = e.reason;
        if (e.reason === 'other' && e.reasonNote.trim()) line.reasonNote = e.reasonNote.trim();
        return line;
      });

      const result = await applyStockCount(lines, note.trim() || undefined);
      setDone(
        result.adjustedLines === 0
          ? `${result.countedLines} counted · everything matched`
          : `${result.countedLines} counted · ${result.adjustedLines} line${
              result.adjustedLines === 1 ? '' : 's'
            } adjusted`,
      );
      setEntries({});
      setNote('');
      setStaleIds(new Set());
      await reload();
      onApplied();
    } catch (e) {
      if (e instanceof StaleCountError) {
        setStaleIds(new Set(e.stale.map((s) => s.productId)));
        setError(e.message);
        // Pull the true numbers back in so the flagged rows show what is real now.
        await reload();
      } else {
        setError(e instanceof Error ? e.message : 'Could not apply that count');
      }
    } finally {
      setBusy(false);
    }
  }

  const visible = rows.filter((r) =>
    search.trim() === ''
      ? true
      : `${r.name} ${r.brand ?? ''} ${r.category ?? ''}`
          .toLowerCase()
          .includes(search.trim().toLowerCase()),
  );

  if (loading) return <p style={{ color: c.muted, fontSize: 14 }}>Loading your products…</p>;

  return (
    <>
      {counted.length > 0 && (
        <p style={{ fontSize: 14, color: c.muted, margin: '0 0 18px' }}>
          {counted.length} counted · {differences.length} difference
          {differences.length === 1 ? '' : 's'} found
        </p>
      )}

      {error && (
        <div
          role="alert"
          style={{
            background: c.dangerBg,
            border: `1px solid ${c.dangerBorder}`,
            borderRadius: 12,
            padding: '12px 16px',
            marginBottom: 16,
            fontSize: 13.5,
            color: c.danger,
          }}
        >
          {error}
        </div>
      )}
      {done && (
        <div
          style={{
            background: c.okBg,
            border: `1px solid ${c.okLine}`,
            borderRadius: 12,
            padding: '12px 16px',
            marginBottom: 16,
            fontSize: 13.5,
            color: c.success,
          }}
        >
          Stock count applied. {done}
        </div>
      )}

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search products"
        style={{ ...field, maxWidth: 320, marginBottom: 14 }}
      />

      <div data-tour="stocktake" style={{ ...cardStyle, padding: 0, overflow: 'hidden', marginBottom: 18 }}>
        <div style={{ overflowX: 'auto' }}>
          {/*
            Fixed layout on purpose. The Reason column only holds a control on
            rows that differ, so with auto layout the whole table reflowed the
            moment a count was typed — columns jumped sideways under the cursor
            mid shelf-walk, which is both jarring and a way to mistype the next
            row. Fixed widths keep every cell where it was.
          */}
          <table
            style={{
              width: '100%',
              minWidth: 760,
              borderCollapse: 'collapse',
              fontSize: 13.5,
              tableLayout: 'fixed',
            }}
          >
            <colgroup>
              <col style={{ width: '34%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '27%' }} />
            </colgroup>
            <thead>
              <tr>
                {['Product', 'System', 'Counted', 'Variance', 'Reason'].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: h === 'Product' || h === 'Reason' ? 'left' : 'right',
                      padding: '11px 16px',
                      fontSize: 12.5,
                      textTransform: 'lowercase',
                      color: c.label,
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((row) => {
                const entry = entryFor(row.id);
                const variance = varianceOf(row);
                const needsReason = variance !== null && variance !== 0;
                const isStale = staleIds.has(row.id);

                return (
                  <tr
                    key={row.id}
                    style={{
                      borderTop: `1px solid ${c.border}`,
                      background: isStale ? c.dangerBg : 'transparent',
                    }}
                  >
                    <td style={{ padding: '10px 16px' }}>
                      <div style={{ fontWeight: 500 }}>{row.name}</div>
                      {isStale && (
                        <div style={{ fontSize: 11.5, color: c.danger, marginTop: 2 }}>
                          Changed while you were counting. Recheck this one
                        </div>
                      )}
                    </td>
                    <td
                      style={{
                        padding: '10px 16px',
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                        color: c.muted2,
                      }}
                    >
                      {row.qtyOnHand}
                    </td>
                    <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                      <input
                        value={entry.counted}
                        onChange={(e) => update(row.id, { counted: e.target.value })}
                        inputMode="numeric"
                        aria-label={`Counted quantity for ${row.name}`}
                        style={{
                          ...field,
                          height: 38,
                          width: 84,
                          textAlign: 'right',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      />
                    </td>
                    <td
                      style={{
                        padding: '10px 16px',
                        textAlign: 'right',
                        fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                        color:
                          variance === null
                            ? '#9A9D93'
                            : variance === 0
                              ? c.success
                              : variance > 0
                                ? '#7A5A12'
                                : c.danger,
                      }}
                    >
                      {variance === null
                        ? '—'
                        : variance === 0
                          ? 'Matches ✓'
                          : `${variance > 0 ? '+' : ''}${variance}`}
                    </td>
                    <td style={{ padding: '10px 16px' }}>
                      {needsReason ? (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <select
                            value={entry.reason}
                            onChange={(e) =>
                              update(row.id, { reason: e.target.value as VarianceReason })
                            }
                            aria-label={`Reason for ${row.name}`}
                            style={{
                              ...field,
                              height: 38,
                              maxWidth: 176,
                              padding: '0 9px',
                              // Unanswered reasons are outlined until chosen.
                              borderColor: entry.reason ? c.borderStrong : '#B98A2E',
                            }}
                          >
                            <option value="">Pick a reason…</option>
                            {VARIANCE_REASONS.map((r) => (
                              <option key={r} value={r}>
                                {VARIANCE_REASON_LABEL[r]}
                              </option>
                            ))}
                          </select>
                          {entry.reason === 'other' && (
                            <input
                              value={entry.reasonNote}
                              onChange={(e) => update(row.id, { reasonNote: e.target.value })}
                              placeholder="Say what happened"
                              aria-label={`Note for ${row.name}`}
                              style={{ ...field, height: 38, maxWidth: 190 }}
                            />
                          )}
                        </div>
                      ) : (
                        <span style={{ color: c.faint }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ ...cardStyle, display: 'grid', gap: 12 }}>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Note for this count (optional), e.g. Monday morning count"
          style={field}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy}
            style={{
              ...primaryButton,
              width: 'auto',
              padding: '0 22px',
              height: 46,
              opacity: busy ? 0.5 : 1,
            }}
          >
            {busy ? 'Applying…' : 'Apply adjustments'}
          </button>
          <button
            type="button"
            onClick={() => {
              setEntries({});
              setNote('');
              setStaleIds(new Set());
              // The count those warnings referred to no longer exists.
              setError(null);
              setNudge(null);
              setDone(null);
            }}
            disabled={busy}
            style={{
              background: 'transparent',
              border: `1px solid ${c.borderStrong}`,
              borderRadius: 980,
              height: 46,
              padding: '0 18px',
              fontSize: 13.5,
              fontFamily: sans,
              color: c.muted2,
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
          {missingReason.length > 0 && (
            <span style={{ fontSize: 13, color: c.danger }}>
              Pick a reason for each difference before applying
            </span>
          )}
        </div>
        {nudge && (
          <p
            role="alert"
            style={{
              background: c.amberBg,
              border: `1px solid ${c.amberLine}`,
              color: c.amberText,
              borderRadius: 12,
              padding: '10px 14px',
              fontSize: 14,
              margin: '12px 0 0',
            }}
          >
            {nudge}
          </p>
        )}
      </div>
    </>
  );
}
